import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Loader2, CheckCircle2, RotateCcw, Bot, Headset, User, Paperclip, Download } from "lucide-react";
import { StatusBadge, PriorityBadge } from "@/components/help/SupportBadges";
import { api, API_BASE } from "@/lib/api";

const SENDER_STYLE = {
  customer: { icon: User, label: "You", wrap: "bg-bg-elevated border-line ml-0 sm:mr-12" },
  agent: { icon: Headset, label: "Support", wrap: "bg-brand-amber/10 border-brand-amber/30 sm:ml-12" },
  system: { icon: Bot, label: "System", wrap: "bg-transparent border-dashed border-line" },
};

export const AttachmentChip = ({ att }) => (
  <a href={`${API_BASE}/helpdesk/attachments/${att.attachment_id}`} target="_blank" rel="noreferrer"
    data-testid={`attachment-${att.attachment_id}`}
    className="mt-2 inline-flex items-center gap-2 rounded-md border border-line bg-bg px-3 py-1.5 text-[11px] text-ink-secondary hover:text-brand-amber transition">
    <Paperclip className="h-3 w-3" /> {att.filename}
    <span className="font-mono text-[9px] text-ink-muted">{(att.size / 1024).toFixed(0)} KB</span>
    <Download className="h-3 w-3" />
  </a>
);

export default function SupportTicketDetail() {
  const { reference } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = () =>
    api.get(`/helpdesk/tickets/${reference}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [reference]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async () => {
    if (reply.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${reference}/reply`, { body: reply.trim() });
      setReply("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send reply"); }
    finally { setBusy(false); }
  };
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/helpdesk/tickets/${reference}/${action}`);
      toast.success(action === "resolve" ? "Marked resolved — thanks!" : "Ticket reopened");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/helpdesk/tickets/${reference}/attachments`, fd);
      toast.success("File attached");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (missing) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-ink-muted mb-3">Ticket not found.</p>
        <Link to="/dashboard/support/tickets" className="text-brand-amber text-sm hover:underline">← My tickets</Link>
      </div>
    );
  }
  if (!data) return <div className="font-mono text-xs text-brand-amber py-10">LOADING…</div>;
  const t = data.ticket;
  const closedish = ["resolved", "closed"].includes(t.status);

  return (
    <div className="max-w-3xl space-y-5" data-testid="ticket-detail-page">
      <Link to="/dashboard/support/tickets" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> My tickets
      </Link>

      <div className="rounded-xl border border-line bg-bg-surface p-5">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="font-mono text-[11px] text-brand-amber" data-testid="ticket-reference">{t.reference}</span>
          <StatusBadge status={t.status} />
          <PriorityBadge priority={t.priority} />
        </div>
        <h1 className="font-display text-xl font-bold text-ink-primary" data-testid="ticket-subject-title">{t.subject}</h1>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 font-mono text-[9px] uppercase tracking-wider text-ink-muted">
          <span>{t.category_label}</span>
          {t.station_name && <span>Station: {t.station_name}</span>}
          {t.affected_feature && <span>Feature: {t.affected_feature}</span>}
          {t.integration_provider && <span>Provider: {t.integration_provider}</span>}
          {t.assignee_name && <span>Agent: {t.assignee_name}</span>}
          <span>Opened {new Date(t.created_at).toLocaleString()}</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          {!closedish && (
            <button type="button" onClick={() => act("resolve")} disabled={busy} data-testid="ticket-resolve-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300 hover:bg-emerald-500/20 transition disabled:opacity-50">
              <CheckCircle2 className="h-3.5 w-3.5" /> Mark resolved
            </button>
          )}
          {t.status === "resolved" && (
            <button type="button" onClick={() => act("reopen")} disabled={busy} data-testid="ticket-reopen-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
              <RotateCcw className="h-3.5 w-3.5" /> Reopen
            </button>
          )}
        </div>
      </div>

      {/* Conversation */}
      <div className="space-y-3" data-testid="ticket-conversation">
        {data.messages.map((m) => {
          const s = SENDER_STYLE[m.sender_type] || SENDER_STYLE.system;
          const Icon = s.icon;
          return (
            <div key={m.message_id} className={`rounded-xl border p-4 ${s.wrap}`}
              data-testid={`ticket-msg-${m.sender_type}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className="h-3.5 w-3.5 text-ink-muted" />
                <span className="text-xs font-semibold text-ink-primary">{m.sender_type === "customer" ? "You" : m.sender_name}</span>
                <span className="font-mono text-[9px] text-ink-muted ml-auto">{new Date(m.created_at).toLocaleString()}</span>
              </div>
              <div className="text-sm text-ink-secondary whitespace-pre-wrap">{m.body}</div>
              {m.attachment && <AttachmentChip att={m.attachment} />}
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Reply box */}
      {t.status !== "closed" ? (
        <div className="rounded-xl border border-line bg-bg-surface p-4">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} data-testid="ticket-reply-input"
            placeholder={closedish ? "Replying will reopen this ticket…" : "Write a reply…"}
            className="w-full rounded-md border border-line bg-bg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60 min-h-[90px]" />
          <div className="flex items-center justify-between mt-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="ticket-attach-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-2 text-xs text-ink-secondary hover:text-ink-primary transition disabled:opacity-50">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
              Attach file
            </button>
            <input ref={fileRef} type="file" className="hidden"
              accept=".png,.jpg,.jpeg,.webp,.pdf,.txt,.csv,.log"
              onChange={(e) => upload(e.target.files?.[0])} />
            <button type="button" onClick={send} disabled={busy || reply.trim().length < 2} data-testid="ticket-reply-btn"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send reply
            </button>
          </div>
          <p className="text-[10px] text-ink-muted mt-2">Attachments: PNG, JPG, WEBP, PDF, TXT, CSV, LOG · max 5 MB each.</p>
        </div>
      ) : (
        <div className="rounded-md border border-line bg-bg-elevated/50 px-4 py-3 text-xs text-ink-muted text-center">
          This ticket is closed. Need more help? <Link to="/dashboard/support/new" className="text-brand-amber hover:underline">Open a new ticket</Link>.
        </div>
      )}
    </div>
  );
}
